// Client/src/components/Navbar.tsx
import { Menu, CheckSquare, LayoutList, Sparkles, Building2, Bell, Timer, Terminal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useEffect, useState } from 'react';
import { countReinigungsaufgaben } from '../services/reinigungService';
import { countFaelligeRollis } from '../services/rolliService';
import { fetchWithAuth } from '../store/fetchClient';
import NotificationSettings from './NotificationSettings';
import { isBackgroundSensitiveMobileClient } from '../utils/pushEnvironment';

interface NavbarProps {
  toggleSidebar: () => void;
}

export default function Navbar({ toggleSidebar }: NavbarProps) {
  const { user, selectedStoreId, stores, isCleaningStaff } = useAuthStore();
  
  // Store-Name aus der Store-ID ermitteln
  const selectedStore = stores.find(store => store.id === selectedStoreId);
  const storeDisplayName = selectedStore?.store_id || selectedStoreId || '';
  const isNativeClient = user?.role === 'tablet';
  const isLayoutDesigner = user?.role === 'layoutdesigner';
  const canShowDefekturCounter = !isCleaningStaff() && !isLayoutDesigner;
  const pauseBackgroundPolling = isBackgroundSensitiveMobileClient();

  // State für Reinigungsplan-Counter
  const [reinigungCounter, setReinigungCounter] = useState({ pending: 0, pendingApproval: 0 });
  const [hideDueRollisFromDashboard, setHideDueRollisFromDashboard] = useState(false);
  const [faelligeRolliCount, setFaelligeRolliCount] = useState(0);
  
  // State für Benachrichtigungseinstellungen
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  // Periodisches Abrufen der Reinigungsplan-Counter
  useEffect(() => {
    if (!selectedStoreId) return;

    const updateReinigungCounter = async () => {
      if (pauseBackgroundPolling && document.visibilityState === 'hidden') {
        return;
      }

      try {
        const counts = await countReinigungsaufgaben(selectedStoreId);
        setReinigungCounter(counts);
      } catch (error) {
        console.error('Fehler beim Abrufen der Reinigungsplan-Counter:', error);
      }
    };

    // Initial laden
    updateReinigungCounter();

    // Alle 30 Sekunden aktualisieren
    const interval = setInterval(updateReinigungCounter, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void updateReinigungCounter();
      }
    };

    if (pauseBackgroundPolling) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      clearInterval(interval);
      if (pauseBackgroundPolling) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [selectedStoreId, pauseBackgroundPolling]);

  // Der Defektur-Zähler wird nur für Märkte mit aktivierter Auslagerung geladen.
  useEffect(() => {
    let active = true;

    if (!selectedStoreId || !canShowDefekturCounter) {
      setHideDueRollisFromDashboard(false);
      setFaelligeRolliCount(0);
      return () => {
        active = false;
      };
    }

    const updateDefekturCounter = async () => {
      if (pauseBackgroundPolling && document.visibilityState === 'hidden') {
        return;
      }

      try {
        const settingsResponse = await fetchWithAuth(
          `/api/stores/${selectedStoreId}/settings/hideDueRollisFromDashboard`,
        );
        if (!settingsResponse.ok) {
          throw new Error('Die Markt-Einstellung konnte nicht geladen werden.');
        }

        const settings: { hideDueRollisFromDashboard?: unknown } = await settingsResponse.json();
        const isEnabled = settings.hideDueRollisFromDashboard === true;

        if (!active) return;
        setHideDueRollisFromDashboard(isEnabled);

        if (!isEnabled) {
          setFaelligeRolliCount(0);
          return;
        }

        const count = await countFaelligeRollis(selectedStoreId);
        if (active) {
          setFaelligeRolliCount(count);
        }
      } catch (error) {
        if (active) {
          console.error('Fehler beim Abrufen des Defektur-Zählers:', error);
          setHideDueRollisFromDashboard(false);
          setFaelligeRolliCount(0);
        }
      }
    };

    setHideDueRollisFromDashboard(false);
    setFaelligeRolliCount(0);
    void updateDefekturCounter();

    const interval = setInterval(updateDefekturCounter, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void updateDefekturCounter();
      }
    };

    if (pauseBackgroundPolling) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      active = false;
      clearInterval(interval);
      if (pauseBackgroundPolling) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [selectedStoreId, pauseBackgroundPolling, canShowDefekturCounter]);

  // Berechne Badge-Zahl nur für fällige Aufgaben (nicht die auf Freigabe wartenden)
  const totalReinigungCount = reinigungCounter.pending;

  return (
    <header className="bg-white shadow-sm border-b">
      {/* Mobile Hamburger & Store Name - nur für nicht-Kiosk */}
      {!isNativeClient && (
        <div className="sm:hidden flex items-center justify-between px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Menü öffnen"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-gray-900">{isLayoutDesigner ? 'Layoutdesigner' : storeDisplayName}</h1>
          </div>

          {!isCleaningStaff() && !isLayoutDesigner && (
            <button
              onClick={() => setShowNotificationSettings(true)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Benachrichtigungseinstellungen"
              title="Push-Benachrichtigungen einrichten"
            >
              <Bell className="h-6 w-6 text-gray-600" />
            </button>
          )}
        </div>
      )}

      {!isNativeClient && isLayoutDesigner && (
        <div className="hidden sm:flex lg:hidden items-center justify-between px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Menü öffnen"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="text-base font-semibold text-gray-900">Layoutdesigner</div>
          <div className="w-10" />
        </div>
      )}

      {/* Desktop Navigation */}
      {!isNativeClient && !isCleaningStaff() && !isLayoutDesigner && (
        <div className="hidden xl:flex items-center justify-between ml-4 space-x-2">
          <div className="flex items-center space-x-2">
            <Link
              to="/"
              className="px-2 py-1 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-sm"
              aria-label="Tagesaufgaben"
            >
              <CheckSquare className="h-5 w-5 text-indigo-600" />
              <span className="ml-1 text-indigo-700 font-medium">Tagesaufgaben</span>
            </Link>

            <Link
              to="/rolli"
              className="px-2 py-1 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-sm"
              aria-label="Defektur"
            >
              <LayoutList className="h-5 w-5 text-indigo-600" />
              <span className="ml-1 text-indigo-700 font-medium">Defektur</span>
              {hideDueRollisFromDashboard && faelligeRolliCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {faelligeRolliCount}
                </span>
              )}
            </Link>

            <Link
              to="/zeiterfassung"
              className="px-2 py-1 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-sm"
              aria-label="Zeiterfassung"
            >
              <Timer className="h-5 w-5 text-indigo-600" />
              <span className="ml-1 text-indigo-700 font-medium">Zeiterfassung</span>
            </Link>

            <Link
              to="/reinigung"
              className="px-2 py-1 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-sm"
              aria-label="Reinigungsplan"
            >
              <Sparkles className="h-5 w-5 text-indigo-600" />
              <span className="ml-1 text-indigo-700 font-medium">Reinigungsplan</span>
              {totalReinigungCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {totalReinigungCount}
                </span>
              )}
            </Link>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setShowNotificationSettings(true)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Benachrichtigungseinstellungen"
              title="Push-Benachrichtigungen einrichten"
            >
              <Bell className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* Standard Tablet (nicht Native-Client): Navigation + Bell */}
      {!isNativeClient && !isCleaningStaff() && !isLayoutDesigner && (
        <div className="hidden sm:flex lg:hidden items-center justify-between px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Menü öffnen"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex items-center space-x-1">
            <Link
              to="/"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Tagesaufgaben"
            >
              <CheckSquare className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Tagesaufgaben</span>
            </Link>

            <Link
              to="/rolli"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Defektur"
            >
              <LayoutList className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Defektur</span>
              {hideDueRollisFromDashboard && faelligeRolliCount > 0 && (
                <span className="ml-0.5 px-1 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {faelligeRolliCount}
                </span>
              )}
            </Link>

            <Link
              to="/zeiterfassung"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Zeiterfassung"
            >
              <Timer className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Zeiterfassung</span>
            </Link>

            <Link
              to="/reinigung"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Reinigungsplan"
            >
              <Sparkles className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Reinigungsplan</span>
              {totalReinigungCount > 0 && (
                <span className="ml-0.5 px-1 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {totalReinigungCount}
                </span>
              )}
            </Link>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setShowNotificationSettings(true)}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Benachrichtigungseinstellungen"
              title="Push-Benachrichtigungen einrichten"
            >
              <Bell className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </div>
      )}

      {/* ToDo Client (Kiosk) für Reinigungskräfte: Nur Übersicht */}
      {isNativeClient && isCleaningStaff() && (
        <div className="flex items-center ml-4 space-x-2">
          <Link 
            to="/reinigung/uebersicht" 
            className="px-2 py-1 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-sm"
            aria-label="Übersicht"
          >
            <Building2 className="h-5 w-5 text-indigo-600" />
            <span className="ml-1 text-indigo-700 font-medium">Übersicht</span>
          </Link>
        </div>
      )}

      {/* Kiosk-Modus: Burger-Menü + Navigation-Links + Store-Name */}
      {isNativeClient && !isCleaningStaff() && (
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Menü öffnen"
            >
              <Menu className="h-6 w-6" />
            </button>
            <Link
              to="/"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Tagesaufgaben"
            >
              <CheckSquare className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Tagesaufgaben</span>
            </Link>

            <Link
              to="/rolli"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Defektur"
            >
              <LayoutList className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Defektur</span>
              {hideDueRollisFromDashboard && faelligeRolliCount > 0 && (
                <span className="ml-0.5 px-1 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {faelligeRolliCount}
                </span>
              )}
            </Link>

            <Link
              to="/zeiterfassung"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Zeiterfassung"
            >
              <Timer className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Zeiterfassung</span>
            </Link>

            <Link
              to="/reinigung"
              className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
              aria-label="Reinigungsplan"
            >
              <Sparkles className="h-4 w-4 text-indigo-600" />
              <span className="ml-0.5 text-indigo-700 font-medium">Reinigungsplan</span>
              {totalReinigungCount > 0 && (
                <span className="ml-0.5 px-1 py-0.5 text-xs font-semibold text-white bg-red-500 rounded-full">
                  {totalReinigungCount}
                </span>
              )}
            </Link>
          </div>

          <div className="text-center">
            <h1 className="text-lg font-semibold text-gray-900">{storeDisplayName}</h1>
          </div>
        </div>
      )}

      {/* Standard Tablet für Reinigungskräfte: Nur Übersicht */}
      {!isNativeClient && isCleaningStaff() && (
        <div className="hidden sm:flex lg:hidden items-center justify-between px-4 py-3">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Menü öffnen"
          >
            <Menu className="h-6 w-6" />
          </button>

          <Link
            to="/reinigung/uebersicht"
            className="px-1 py-1.5 border border-indigo-200 rounded-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center text-xs"
            aria-label="Übersicht"
          >
            <Building2 className="h-4 w-4 text-indigo-600" />
            <span className="ml-0.5 text-indigo-700 font-medium">Übersicht</span>
          </Link>
        </div>
      )}
      
      
      {/* Benachrichtigungseinstellungen Modal */}
      <NotificationSettings 
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
      />
    </header>
  );
}
